import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Paper, Box, TextField, Button, Chip, Tooltip, Typography } from '@mui/material'
import { DataGrid, type GridColDef, type GridColumnHeaderParams, type GridRenderCellParams } from '@mui/x-data-grid'
import HomeIcon from '@mui/icons-material/Home'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import DataObjectIcon from '@mui/icons-material/DataObject'

import { type CurrencyExchangeOrdersSearchRequest } from '../../../../api-schema-types/financial/CurrencyExchangeDomainDtos'
import { useCurrencyExchangeApiSearchExchangeOrders } from '../../../../api-services/financial/currencyExchangeApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'

import { formatDateStringAsApiFormat } from '../../../../utility/formatters/DateFormatter'
import { NumberFormatter } from '../../../../utility/formatters/NumberFormatter'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'

const CurrencyExchangeOrdersPage: React.FC = () => {
  const history = useHistory()
  const goTo = (location: string): void => {
    history.push(location)
  }

  const [page, setPage] = useState<number>(0)
  const pageSize: number = 50

  const createSearchRequest = (search?: string | null): CurrencyExchangeOrdersSearchRequest => {
    if (search === undefined || search === null) {
      return {
        members: undefined,
        orderId: undefined,
        page,
        pageSize
      }
    }

    if (Number.isInteger(Number(search))) {
      return {
        members: undefined,
        orderId: Number(search),
        page,
        pageSize
      }
    }

    return {
      members: search,
      orderId: undefined,
      page,
      pageSize
    }
  }
  const [searchQuery, setSearchQuery] = useState<CurrencyExchangeOrdersSearchRequest>(createSearchRequest(null))

  const searchExchangeOrdersApiResult = useCurrencyExchangeApiSearchExchangeOrders(searchQuery)

  function renderTransactionFee (params: GridRenderCellParams): React.ReactFragment {
    if ((Boolean(params.value)) && params.value > 0) {
      return <span>{NumberFormatter.roundToString(params.value, 8) }</span>
    }
    return <span>-</span>
  }

  const handleSearchQueryChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const searchQuery: string = event.target.value
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      setSearchQuery(createSearchRequest(searchQuery))
    }
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, editable: true },
    {
      field: 'state',
      headerName: 'State',
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value.toLowerCase() === 'filled' && params.row.reattemptCount === 0) {
          return <Chip label={params.value.toUpperCase()} color={'success'} variant="outlined" />
        } else if (params.value.toLowerCase() === 'filled' && params.row.reattemptCount > 0) {
          return <Chip label={params.value.toUpperCase()} color={'warning'} variant="outlined" />
        } else if (params.value.toLowerCase() === 'cancelled' || params.value.toLowerCase() === 'failed') {
          return <Chip label={params.value.toUpperCase()} color={'error'} variant="outlined" />
        } else {
          return <Chip label={params.value.toUpperCase()} variant="outlined" />
        }
      }
    },
    { field: 'memberId', headerName: 'Member ID', width: 100, editable: false },
    { field: 'memberFullName', headerName: 'Full Name', width: 150, editable: false },
    { field: 'memberEmailAddress', headerName: 'Email Address', width: 150, editable: false },
    { field: 'cellOrderId', headerName: 'Cell Order ID', width: 100, editable: false },
    { field: 'reattemptCount', headerName: 'Re-attempts', width: 100, editable: false },
    { field: 'projectName', headerName: 'Project', width: 150, editable: false },
    {
      field: 'requestedOrderAmount',
      headerName: 'Order Amount',
      width: 120,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{NumberFormatter.roundToString(params.value, 8)}</span>
      }
    },
    {
      field: 'currencyPair',
      headerName: 'Currency Pair',
      width: 100,
      editable: false
    },
    {
      field: 'indicativeExchangeRate',
      headerName: 'Indicative Rate',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <span>
            <Tooltip sx={{ alignContent: 'center' }} title="The exchange rate shown to the member at time of purchase">
              <InfoIcon fontSize="small" style={{ color: '#ddd' }} />
            </Tooltip>{params.colDef.headerName}
          </span>
        )
      },
      width: 150,
      editable: false
    },
    {
      field: 'consumedExchangeRate',
      headerName: 'Consumed Rate',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <span>
            <Tooltip sx={{ alignContent: 'center' }} title="The exchange rate at which the trade executed">
              <InfoIcon fontSize="small" style={{ color: '#ddd' }} />
            </Tooltip>{params.colDef.headerName}
          </span>
        )
      },
      width: 150,
      editable: false
    },
    {
      field: 'requestedTransactionFee',
      headerName: 'Txn Fee',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <span>
             <Tooltip sx={{ alignContent: 'center' }} title="This will be zero as we are not passing on the VALR transaction fee to the customer">
                <InfoIcon fontSize="small" style={{ color: '#ddd' }} />
             </Tooltip>{params.colDef.headerName}
          </span>
        )
      },
      width: 120,
      editable: false,
      renderCell: renderTransactionFee
    },
    {
      field: 'internalTransactionAmount',
      headerName: 'Internal Txn Amount',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <span>
            <Tooltip sx={{ alignContent: 'center' }} title="The order amount at the consumed exchange rate">
              <InfoIcon fontSize="small" style={{ color: '#ddd' }} />
            </Tooltip>{params.colDef.headerName}
          </span>
        )
      },
      width: 160,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{NumberFormatter.roundToString(params.value, 8)}</span>
      }
    },
    {
      field: 'internalTransactionFee',
      headerName: 'Internal Txn Fee',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <span>
             <Tooltip sx={{ alignContent: 'center' }} title="This will be zero">
               <InfoIcon fontSize="small" style={{ color: '#ddd' }}/>
            </Tooltip>{params.colDef.headerName}
          </span>
        )
      },
      width: 130,
      editable: false,
      renderCell: renderTransactionFee
    },
    {
      field: 'surplusConvertedAmount',
      headerName: 'Surplus',
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <span>
            <Tooltip sx={{ alignContent: 'center' }} title="Surplus ZAR in the VALR account due to us executing at a “better” rate than the indicative rate">
              <InfoIcon fontSize="small" style={{ color: '#ddd' }} />
            </Tooltip>{params.colDef.headerName}
          </span>
        )
      },
      width: 100,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{NumberFormatter.roundToString(params.value, 2)}</span>
      }
    },
    {
      field: 'orderPlacedAt',
      headerName: 'Placed',
      width: 200,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{formatDateStringAsApiFormat(params.value)}</span>
      }
    },
    {
      field: 'exchangeOrderReference',
      headerName: 'Exchange Order Reference',
      width: 310,
      editable: false
    },
    {
      field: 'orderFilledAt',
      headerName: 'Filled',
      width: 200,
      editable: false,
      renderCell: (params: GridRenderCellParams<string>) => {
        return <span>{formatDateStringAsApiFormat(params.value)}</span>
      }
    },
    {
      field: 'requestData',
      headerName: 'Request Data',
      width: 400,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value != null) {
          return (
            <>
              <Tooltip title={<div><pre>{JSON.stringify(params.value, null, 2) }</pre></div>}>
                <DataObjectIcon fontSize="small" color={'primary'} sx={{ mr: 1 }}/>
              </Tooltip>
              <Typography variant={'body2'}>{'Hover to view Request Data'}</Typography>
            </>
          )
        } else {
          return (
            <>
              <Chip color={'error'} label={'No Request Data to Display'} variant="outlined" />
            </>
          )
        }
      }
    },
    {
      field: 'responseData',
      headerName: 'Response Data',
      width: 400,
      editable: false,
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value != null) {
          return (
            <>
              <Tooltip title={<div><pre>{JSON.stringify(params.value, null, 2) }</pre></div>}>
                <DataObjectIcon fontSize="small" color={'primary'} sx={{ mr: 1 }}/>
              </Tooltip>
              <Typography variant={'body2'}>{'Hover to view Response Data'}</Typography>
            </>
          )
        } else {
          return (
            <>
              <Chip color={'error'} label={'No Response Data to Display'} variant="outlined" />
            </>
          )
        }
      }
    },
    {
      field: 'failedReason',
      headerName: 'Failed Reason',
      width: 400,
      editable: false
    }

  ]

  return (
    <>
    <Box sx={{ mb: 4, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
      <Typography variant={'h4'}>VALR Currency Exchange Orders</Typography>
      <Button variant={'text'} type="button" onClick={() => { goTo('/app/finance-area') }}>
        <HomeIcon style={{ color: '#444' }} />
      </Button>
    </Box>
      <Paper>
        <Box sx={{ p: 4, mt: 3 }}>
          <TextField
            label="Enter member's first name, last name, email address or order id."
            type={'text'}
            variant="outlined"
            sx={{ width: '100%', mb: 2 }}
            placeholder=""
            onChange={handleSearchQueryChange}
          />
        <Box sx={{ p: 4, mt: 3 }}>
          {searchExchangeOrdersApiResult.isLoading && <LoadingComponent />}
          {(searchExchangeOrdersApiResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(searchExchangeOrdersApiResult.error)} />}
          { searchExchangeOrdersApiResult.isSuccess && (
                <DataGrid
                  rows={searchExchangeOrdersApiResult.data}
                  columns={columns}
                  autoHeight={true}
                  onPageChange={newPage => {
                    setPage(newPage)
                  }}
                  page={page}
                  pageSize={pageSize}
                  rowsPerPageOptions={[pageSize]}
                  rowCount={searchExchangeOrdersApiResult.data.length}
                  paginationMode="server"
                  pagination
                />
          )}
            </Box>
        </Box>
      </Paper>
    </>
  )
}

export default CurrencyExchangeOrdersPage
